import instance from 'api';
import { AxiosError, AxiosResponse } from 'axios';
import { ApiResponse, BaseApi, Response } from 'baseApi';
import { endpoints } from 'endpoints.config';
import { SyncWondeDataResponse, WondeLinkStatusError } from 'pages/wondeIntegration/models';

const UNKNOWN_ERROR_MESSAGE = 'Sorry, something went wrong. Please try again.';

export class WondeApi extends BaseApi {
  //check access/link status
  public static checkLinkStatus = async (): Promise<any> => {
    try {
      const result: AxiosResponse<ApiResponse<any>> = await instance.get(
        endpoints.wondeIntegration.checkLinkStatus
      );

      if (WondeApi.isResponseSuccessful(result.data)) {
        const data = result.data.details;
        return WondeApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = WondeLinkStatusError.parseError(errors);
          return WondeApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = WondeLinkStatusError.parseError(errors);
          return WondeApi.errorResponse(message);
        }
      }
      return WondeApi.errorResponse(UNKNOWN_ERROR_MESSAGE);
    }
  };

  //get school list to select from for linkage
  public static getSchoolLinkList = async (educationNumber: string): Promise<any> => {
    try {
      const result: AxiosResponse<ApiResponse<any>> = await instance.get(
        endpoints.wondeIntegration.getSchoolLinkList + `?urn=${educationNumber}`
      );

      if (WondeApi.isResponseSuccessful(result.data)) {
        const data = result.data.details;
        return WondeApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = WondeLinkStatusError.parseError(errors);
          return WondeApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = WondeLinkStatusError.parseError(errors);
          return WondeApi.errorResponse(message);
        }
      }
      return WondeApi.errorResponse(UNKNOWN_ERROR_MESSAGE);
    }
  };

  //request access from selected school
  public static requestAccessFromSelectedSchool = async (selectedSchoolId: string | null, schoolAddress: string | null): Promise<any> => {
    try {
      const payload = { 
        wondeSchoolId: selectedSchoolId,
        SchoolUsersEmailAddress: schoolAddress,
      };

      const result: AxiosResponse<ApiResponse<any>> = await instance.post(
        endpoints.wondeIntegration.requestAccess,
        payload
      );

      if (WondeApi.isResponseSuccessful(result.data)) {
        const data = result.data.details;
        return WondeApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = WondeLinkStatusError.parseError(errors);
          return WondeApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = WondeLinkStatusError.parseError(errors);
          return WondeApi.errorResponse(message);
        }
      }
      return WondeApi.errorResponse(UNKNOWN_ERROR_MESSAGE);
    }
  };

  //sync wonde data
  public static syncWondeData = async (
    wondeSchoolId: string | null,
    selectedSyncType: string
  ): Promise<Response<SyncWondeDataResponse | null>> => {
    try {
      const payload = { 
        wondeSchoolId: wondeSchoolId,
        dataType: selectedSyncType,
      };

      const result: AxiosResponse<ApiResponse<any>> = await instance.post(
        endpoints.wondeIntegration.syncData,
        payload
      );

      if (WondeApi.isResponseSuccessful(result.data)) {
        const data = result.data.details;
        return WondeApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = WondeLinkStatusError.parseError(errors);
          return WondeApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = WondeLinkStatusError.parseError(errors);
          return WondeApi.errorResponse(message);
        }
      }
      return WondeApi.errorResponse(UNKNOWN_ERROR_MESSAGE);
    }
  }; 
  
  //get list of pupil records
  public static wondePupilRecords = async (): Promise<any> => {
    try {
      const result: AxiosResponse<ApiResponse<any>> = await instance.get(
        endpoints.wondeIntegration.compareAcademicYears
      );

      if (WondeApi.isResponseSuccessful(result.data)) {
        const data = result.data.details;
        return WondeApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = WondeLinkStatusError.parseError(errors);
          return WondeApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = WondeLinkStatusError.parseError(errors);
          return WondeApi.errorResponse(message);
        }
      }
      return WondeApi.errorResponse(UNKNOWN_ERROR_MESSAGE);
    }
  };

  //submit selected pupil records
  public static submitSelectedWondePupilRecords = async (wondeSchoolId: any, selectedSyncType: string): Promise<any> => {
    try {
      const payload = {
        wondeSchoolId: wondeSchoolId,
        dataType: selectedSyncType,
      };

      const result: AxiosResponse<ApiResponse<any>> = await instance.post(
        endpoints.wondeIntegration.syncMatchedData,
        payload
      );

      if (WondeApi.isResponseSuccessful(result.data)) {
        const data = result.data.details;
        return WondeApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = WondeLinkStatusError.parseError(errors);
          return WondeApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = WondeLinkStatusError.parseError(errors);
          return WondeApi.errorResponse(message);
        }
      }
      return WondeApi.errorResponse(UNKNOWN_ERROR_MESSAGE);
    }
  };  

  //submit selected wonde exact matches
  public static submitSelectedWondeExactMatches = async (selectedIds: any, url: string): Promise<any> => {
    try {
      const payload = { matchedPairs: selectedIds };

      const result: AxiosResponse<ApiResponse<any>> = await instance.post(
        url,
        payload
      );

      if (WondeApi.isResponseSuccessful(result.data)) {
        const data = result.data.details;
        return WondeApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = WondeLinkStatusError.parseError(errors);
          return WondeApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = WondeLinkStatusError.parseError(errors);
          return WondeApi.errorResponse(message);
        }
      }
      return WondeApi.errorResponse(UNKNOWN_ERROR_MESSAGE);
    }
  };
  
  //get list of wonde unmatched records
  public static getWondeUnmatchedRecords = async (url: string): Promise<any> => {
    try {
      const result: AxiosResponse<ApiResponse<any>> = await instance.get(
        url
      );

      if (WondeApi.isResponseSuccessful(result.data)) {
        const data = result.data.details;
        return WondeApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = WondeLinkStatusError.parseError(errors);
          return WondeApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = WondeLinkStatusError.parseError(errors);
          return WondeApi.errorResponse(message);
        }
      }
      return WondeApi.errorResponse(UNKNOWN_ERROR_MESSAGE);
    }
  };

  //submit selected matched wonde records
  public static submitSelectedMatchedWondeRecords = async (selectedRecords: any, url: string): Promise<any> => {
    try {
      const payload = { matchedPairs: selectedRecords };

      const result: AxiosResponse<ApiResponse<any>> = await instance.post(
        url,
        payload
      );

      if (WondeApi.isResponseSuccessful(result.data)) {
        const data = result.data.details;
        return WondeApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = WondeLinkStatusError.parseError(errors);
          return WondeApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = WondeLinkStatusError.parseError(errors);
          return WondeApi.errorResponse(message);
        }
      }
      return WondeApi.errorResponse(UNKNOWN_ERROR_MESSAGE);
    }
  };   

  //submit selected remaining wonde records
  public static submitSelectedWondeRecords = async (selectedIds: any, url: string): Promise<Response<any>> => {
    try {
      const payload = selectedIds;

      const result: AxiosResponse<ApiResponse<any>> = await instance.post(
        url,
        payload
      );

      if (WondeApi.isResponseSuccessful(result.data)) {
        const data = result.data.details;
        return WondeApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = WondeLinkStatusError.parseError(errors);
          return WondeApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error?.response?.data?.errors) {
        const errors = error.response.data.errors;
          return WondeApi.errorResponse('FormError', errors);
      } 
      return WondeApi.errorResponse(UNKNOWN_ERROR_MESSAGE);
    }
  };
  
  //delete remaining get set records
  public static deleteRemainingGetSetRecords = async (selectedIds: any, url: string): Promise<any> => {
    try {
      const payload = { idsToDelete: selectedIds };

      const result: AxiosResponse<ApiResponse<any>> = await instance.post(
        url,
        payload
      );

      if (WondeApi.isResponseSuccessful(result.data)) {
        const data = result.data.details;
        return WondeApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = WondeLinkStatusError.parseError(errors);
          return WondeApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = WondeLinkStatusError.parseError(errors);
          return WondeApi.errorResponse(message);
        }
      }
      return WondeApi.errorResponse(UNKNOWN_ERROR_MESSAGE);
    }
  };  
};